// The device's copy of a lot after an offline Save (BL-2431, ADR 066 — Offline Mode § 6.3): the
// queued fields written onto the cached detail and list row, so reopening the lot offline shows
// them. Names the pickers show (seller, rep, tax rate, location) come from the synced reference.
import type { OfflineReferenceDto } from '@auctogon/core-admin/schemas/responses/offline-reference';
import type { OfflineDeltaLotDto, OfflineInventoryListItemDto } from '@auctogon/core-admin/schemas/responses/offline-sync';
import type { LotDetailPayload } from '@auctogon/core-admin/schemas/responses/lot-detail';
import type { InventoryListItemDto } from '@auctogon/core-admin/schemas/responses/inventory-list';
import { resolveEffectiveSalesRep } from '@auctogon/core-admin/services/sales-rep';

import type { LotPatchFields } from './offline-lot-edit';

/** The detail fields a Save writes as sent, under the same name. */
const DETAIL_FIELDS = [
  'name', 'description', 'categoryId', 'make', 'model', 'year', 'vin', 'serialNumber', 'hoursMileage',
  'mileageUnit', 'startPrice', 'reserve', 'taxStatus', 'isTitledItem', 'titleStatus', 'titleState',
  'titleMailingStatus', 'titleMailingTrackingNumber', 'titleMailingDateSent', 'titleMailingRecipientName',
  'titleMailingRecipientAddress', 'hasLoadOutFee', 'loadOutFee', 'acreage', 'pricingType',
  'customFields', 'visibleToSeller', 'showSalesRep', 'showSeller', 'showAdditionalContact',
  'additionalContactTitle', 'additionalContactName', 'additionalContactPhone', 'additionalContactEmail',
] as const;

/** The list-row fields a Save writes as sent, under the same name. */
const ROW_FIELDS = [
  'name', 'description', 'categoryId', 'make', 'model', 'year', 'vin', 'serialNumber', 'hoursMileage',
  'mileageUnit', 'startPrice', 'reserve', 'taxStatus', 'isTitledItem', 'titleStatus', 'titleState', 'acreage',
  'pricingType',
] as const;

/** The form's "none" (an empty text) is the server's null. */
function stored(value: unknown): unknown {
  return value === '' ? null : value;
}

function pick(changes: LotPatchFields, fields: readonly string[]): LotPatchFields {
  return Object.fromEntries(fields.filter((f) => f in changes).map((f) => [f, stored(changes[f])]));
}

/** The seller the picker chose, from the synced sellers. */
function sellerPatch(sellerId: string, reference: OfflineReferenceDto) {
  const seller = reference.sellers?.find((s) => s.id === sellerId) ?? null;
  const detail: Partial<LotDetailPayload> = {
    sellerId,
    sellerIdentity: seller?.identity ?? null,
    sellerSalesRepId: seller?.salesRepId ?? null,
    sellerSalesRepName: seller?.salesRepName ?? null,
  };
  const row: Partial<InventoryListItemDto> = {
    sellerId,
    sellerCustomerId: sellerId,
    seller: seller ? { ...seller.identity, id: seller.id, customerNumber: seller.customerNumber ?? '' } : null,
    sellerSalesRepId: seller?.salesRepId ?? null,
    sellerSalesRepName: seller?.salesRepName ?? null,
  };
  return { detail, row };
}

/**
 * The lot's effective rep (lot override, else the seller's, else House) through the one resolver
 * the server uses. The seller's rep arrives already formatted (the picker row's name), so it is
 * passed as the name. A lot override missing from the synced reps (archived since) keeps the name
 * the device last had.
 */
function effectiveRep(detail: LotDetailPayload, reference: OfflineReferenceDto): string {
  const lotRep = detail.salesRepId == null ? null : (reference.salesReps?.find((r) => r.id === detail.salesRepId) ?? null);
  if (detail.salesRepId != null && lotRep == null) return detail.rep;
  const sellerRep = detail.sellerSalesRepName == null ? null : { firstName: detail.sellerSalesRepName, lastName: null, deletedAt: null };
  return resolveEffectiveSalesRep({
    lotOverride: { id: detail.salesRepId, rep: lotRep },
    sellerRep: { id: detail.sellerSalesRepId, rep: sellerRep },
  }).displayName;
}

/**
 * Write an offline Save's changed fields onto the device's copy of the lot.
 *
 * @param input.lot - the cached lot (detail, list row and notes).
 * @param input.changes - the fields the Save changed (`diffLotPatch`), as the PATCH spells them.
 * @param input.reference - the synced picker lists, for the names the screens show.
 * @returns the lot as the offline screens should now show it.
 */
export function applyLotEditOverlay(input: {
  lot: OfflineDeltaLotDto;
  changes: LotPatchFields;
  reference: OfflineReferenceDto;
}): OfflineDeltaLotDto {
  const { lot, changes, reference } = input;
  let detail: LotDetailPayload = { ...lot.detail, ...pick(changes, DETAIL_FIELDS) };
  let row: OfflineInventoryListItemDto = { ...lot.row, ...pick(changes, ROW_FIELDS) };
  if (typeof changes.sellerId === 'string' && changes.sellerId !== '') {
    const seller = sellerPatch(changes.sellerId, reference);
    detail = { ...detail, ...seller.detail };
    row = { ...row, ...seller.row };
  }
  if ('salesRepId' in changes) {
    detail = { ...detail, salesRepId: (stored(changes.salesRepId) as string | null) ?? null };
    row = { ...row, salesRepId: detail.salesRepId };
  }
  if ('taxRateId' in changes) {
    const rate = reference.taxRates?.find((r) => r.id === changes.taxRateId) ?? null;
    detail = { ...detail, taxRate: rate };
    row = { ...row, taxRate: rate };
  }
  if ('locationId' in changes) {
    const locationId = (stored(changes.locationId) as string | null) ?? null;
    const location = reference.locations?.find((l) => l.id === locationId) ?? null;
    detail = { ...detail, locationId, location: location ? (location.name ?? location.address) : null };
    row = { ...row, locationId: locationId ?? undefined };
  }
  if ('customFields' in changes) row = { ...row, customFieldValues: detail.customFields };
  if (!('salesRepId' in changes) && !('sellerId' in changes)) return { ...lot, detail, row };
  const rep = effectiveRep(detail, reference);
  return { ...lot, detail: { ...detail, rep }, row: { ...row, effectiveSalesRepName: rep } };
}
